import { useCallback, useEffect, useMemo, useState } from 'react'
import type { LoginResponse } from '../api/auth'
import { fetchDepartments, type Department } from '../api/catalog'
import { userDirectoryApi } from '../api/userDirectory'
import { CloneConfigurationButton, useOpenAfterClone } from '../components/CloneConfigurationButton'
import {
  ACTIVITY_BENEFICIARY_LABELS,
  ACTIVITY_EVENT_LABELS,
  activityPayoutsApi,
  type ActivityPayoutBeneficiary,
  type ActivityPayoutEvent,
  type ActivityPayoutList,
  type ActivityPayoutRule,
  type ActivityPayoutRuleBody,
  type ActivityPayoutRunResult,
} from '../api/activityPayouts'
import {
  FormGrid,
  StatGrid, FormSection, Page, PageHeader, Tabs, Callout, Card, CardHeader, CardContent, StatCard, DataTable, DataTableEmptyRow,
  LoadingState, StatusPill, Dialog, Button, IconButton, TextField, Select, type PillTone, FilterBar,
} from '../components/ui'

/**
 * F-083 (TASK-0337): provvigioni da attivita'. L'Admin configura compensi FISSI per evento (nessuna
 * percentuale sul ricavo) e li fa maturare con un'esecuzione preceduta da un'anteprima; ognuno vede il proprio
 * maturato. Gli importi compaiono solo a chi ha il permesso (il server omette la chiave): qui si mostra "—".
 */
type Tab = 'rules' | 'run' | 'accruals'

const EVENTS = Object.keys(ACTIVITY_EVENT_LABELS) as ActivityPayoutEvent[]
const BENEFICIARIES = Object.keys(ACTIVITY_BENEFICIARY_LABELS) as ActivityPayoutBeneficiary[]

function isAdminRole(auth: LoginResponse): boolean {
  return auth.role === 'Administrator' || auth.role === 'EqualAdmin'
}

function money(value: number | undefined): string {
  return value === undefined ? '—' : value.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })
}

function isoDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function monthStart(): string {
  const d = new Date()
  return isoDay(new Date(d.getFullYear(), d.getMonth(), 1))
}

function errorText(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback
}

const STATUS_TONE: Record<string, PillTone> = {
  Bozza: 'neutral',
  Attiva: 'success',
  Archiviata: 'neutral',
  Maturata: 'success',
  Stornata: 'error',
}

interface RuleForm {
  id?: number
  code: string
  name: string
  event: ActivityPayoutEvent
  beneficiary: ActivityPayoutBeneficiary
  amount: string
  departmentId: string
  productId: string
  validFrom: string
  validTo: string
  priority: string
}

const EMPTY_RULE: RuleForm = {
  code: '',
  name: '',
  event: 'ContrattoAttivato',
  beneficiary: 'SalesCloser',
  amount: '',
  departmentId: '',
  productId: '',
  validFrom: isoDay(new Date()),
  validTo: '',
  priority: '0',
}

export function ActivityPayoutsPage({ auth }: { auth: LoginResponse }) {
  const admin = isAdminRole(auth)
  const [tab, setTab] = useState<Tab>(admin ? 'rules' : 'accruals')
  const [departments, setDepartments] = useState<Department[]>([])
  const [names, setNames] = useState<Record<number, string>>({})

  useEffect(() => {
    void fetchDepartments().then(setDepartments).catch(() => setDepartments([]))
    if (admin) {
      void userDirectoryApi.list({ activeOnly: false, limit: 200 })
        .then(page => setNames(Object.fromEntries(page.map(u => [u.id, u.name]))))
        .catch(() => setNames({}))
    }
  }, [admin])

  const userLabel = useCallback((id: number) => names[id] ?? `Utente #${id}`, [names])
  const departmentLabel = useCallback(
    (id: number | null) => (id === null ? 'Tutti' : departments.find(d => d.id === id)?.name ?? `#${id}`),
    [departments],
  )

  return (
    <Page>
      <PageHeader
        title="Compensi da attività"
        icon="paid"
        tabs={
          <Tabs
            aria-label="Sezioni dei compensi da attività"
            items={[
              ...(admin ? [{ id: 'rules', label: 'Regole' }, { id: 'run', label: 'Esecuzione' }] : []),
              { id: 'accruals', label: 'Maturato' },
            ]}
            value={tab}
            onChange={id => setTab(id as Tab)}
          />
        }
      />

      <Callout tone="info">
        Compensi fissi per attività svolte: appuntamento effettuato, contratto generato, contratto attivato. Non sono
        percentuali sul ricavo e non entrano nel pagamento delle provvigioni.
      </Callout>

      {tab === 'rules' && admin && <RulesTab departments={departments} departmentLabel={departmentLabel} />}
      {tab === 'run' && admin && <RunTab />}
      {tab === 'accruals' && <AccrualsTab admin={admin} userLabel={userLabel} />}
    </Page>
  )
}

// ---- Regole ---------------------------------------------------------------------------------------

function RulesTab({
  departments,
  departmentLabel,
}: {
  departments: Department[]
  departmentLabel: (id: number | null) => string
}) {
  const [rules, setRules] = useState<ActivityPayoutRule[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [form, setForm] = useState<RuleForm | null>(null)
  const [modalError, setModalError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setRules(await activityPayoutsApi.listRules())
    } catch (e) {
      setError(errorText(e, 'Errore nel caricamento'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  function openEdit(r: ActivityPayoutRule) {
    setModalError(null)
    setForm({
      id: r.id,
      code: r.code,
      name: r.name,
      event: r.event,
      beneficiary: r.beneficiary,
      amount: r.amount === undefined ? '' : String(r.amount),
      departmentId: r.departmentId === null ? '' : String(r.departmentId),
      productId: r.productId === null ? '' : String(r.productId),
      validFrom: r.validFrom,
      validTo: r.validTo ?? '',
      priority: String(r.priority),
    })
  }

  // F-100: dopo "Clona" la copia (Bozza v1) compare nell'elenco e si apre in modifica.
  const [cloneNotice, setCloneNotice] = useState('')
  const openAfterClone = useOpenAfterClone(rules, r => openEdit(r))

  async function save() {
    if (!form) return
    const amount = Number(form.amount.replace(',', '.'))
    if (!form.name.trim()) return setModalError('Il nome è obbligatorio')
    if (form.id === undefined && !form.code.trim()) return setModalError('Il codice è obbligatorio')
    if (!(amount > 0)) return setModalError('L\'importo deve essere maggiore di zero')
    if (!form.validFrom) return setModalError('Indica l\'inizio validità')

    const body: ActivityPayoutRuleBody = {
      code: form.code.trim(),
      name: form.name.trim(),
      event: form.event,
      beneficiary: form.beneficiary,
      amount,
      departmentId: form.departmentId ? Number(form.departmentId) : null,
      productId: form.productId ? Number(form.productId) : null,
      validFrom: form.validFrom,
      validTo: form.validTo || null,
      priority: Number(form.priority) || 0,
    }

    setSaving(true)
    setModalError(null)
    try {
      if (form.id === undefined) await activityPayoutsApi.createRule(body)
      else await activityPayoutsApi.updateRule(form.id, body)
      setForm(null)
      await load()
    } catch (e) {
      setModalError(errorText(e, 'Errore nel salvataggio'))
    } finally {
      setSaving(false)
    }
  }

  async function act(action: () => Promise<unknown>) {
    setError(null)
    try {
      await action()
      await load()
    } catch (e) {
      setError(errorText(e, 'Operazione non riuscita'))
    }
  }

  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
        <span className="text-muted">
          Una regola attiva non si modifica: si crea una nuova versione, che attivandosi archivia la precedente. Il
          maturato già creato non cambia.
        </span>
        <Button variant="filled" icon="add" onClick={() => { setModalError(null); setForm(EMPTY_RULE) }}>
          Nuova regola
        </Button>
      </div>

      {error && <Callout tone="error">{error}</Callout>}
      {cloneNotice && (
        <Callout
          tone="info"
          action={<IconButton icon="close" aria-label="Chiudi" size="sm" onClick={() => setCloneNotice('')} />}
        >
          {cloneNotice}
        </Callout>
      )}

      {loading ? (
        <LoadingState />
      ) : (
        <DataTable>
          <thead>
            <tr>
              <th>Codice</th><th>Evento</th><th>Beneficiario</th><th className="md-num">Importo</th><th>Reparto</th>
              <th>Prodotto</th><th>Validità</th><th>Priorità</th><th>Versione</th><th>Stato</th><th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && <DataTableEmptyRow colSpan={11} icon="payments" title="Nessuna regola configurata." />}
            {rules.map(r => (
              <tr key={r.id}>
                <td className="md-typescale-emphasized-body-medium">{r.code}<div className="md-typescale-body-small text-muted">{r.name}</div></td>
                <td>{ACTIVITY_EVENT_LABELS[r.event]}</td>
                <td>{ACTIVITY_BENEFICIARY_LABELS[r.beneficiary]}</td>
                <td className="md-num">{money(r.amount)}</td>
                <td>{departmentLabel(r.departmentId)}</td>
                <td>{r.productId === null ? 'Tutti' : `#${r.productId}`}</td>
                <td>{r.validFrom} → {r.validTo ?? 'aperta'}</td>
                <td>{r.priority}</td>
                <td>v{r.version}</td>
                <td><StatusPill tone={STATUS_TONE[r.status] ?? 'neutral'}>{r.status}</StatusPill></td>
                <td className="text-nowrap">
                  <CloneConfigurationButton
                    entityLabel="regola di compenso"
                    url={`/api/commissions/activity-payout-rules/${r.id}/clone`}
                    defaultName={r.name}
                    extraFields={[{ key: 'code', label: 'Codice della copia', required: true, placeholder: 'es. APPUNTAMENTO_BIS' }]}
                    note="Si copiano evento, beneficiario, importo, ambito, validità e priorità. La copia nasce in bozza, versione 1, con il nuovo codice."
                    onCloned={async id => { await load(); setCloneNotice('Copia creata: è inattiva (bozza v1). Controllala e attivala quando è pronta.'); openAfterClone(id) }}
                  />
                  {r.status === 'Bozza' && (
                    <>
                      <Button variant="outlined" size="sm" className="me-1" onClick={() => openEdit(r)}>Modifica</Button>
                      <Button variant="tonal" size="sm" className="me-1" onClick={() => void act(() => activityPayoutsApi.activateRule(r.id))}>Attiva</Button>
                      <IconButton icon="delete" size="sm" aria-label="Elimina bozza" onClick={() => void act(() => activityPayoutsApi.deleteRule(r.id))} />
                    </>
                  )}
                  {r.status !== 'Bozza' && (
                    <>
                      <Button variant="outlined" size="sm" className="me-1" onClick={() => void act(() => activityPayoutsApi.newVersion(r.id))}>Nuova versione</Button>
                      {r.status === 'Attiva' && (
                        <Button variant="outlined" size="sm" onClick={() => void act(() => activityPayoutsApi.archiveRule(r.id))}>Archivia</Button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}

      <Dialog
        open={form !== null}
        onClose={() => setForm(null)}
        dismissible={!saving}
        title={form === null ? '' : form.id === undefined ? 'Nuova regola' : `Modifica bozza ${form.code}`}
        as="form"
        onSubmit={e => { e.preventDefault(); void save() }}
        actions={
          <>
            <Button variant="text" disabled={saving} onClick={() => setForm(null)}>Annulla</Button>
            <Button type="submit" variant="text" disabled={saving}>
              {saving ? 'Salvataggio...' : 'Salva bozza'}
            </Button>
          </>
        }
      >
        {form && (
          <>
            {modalError && <Callout tone="error">{modalError}</Callout>}
            <FormSection title="Regola">
              <TextField id="ap-code" label="Codice *" maxLength={50} value={form.code}
                disabled={saving || form.id !== undefined}
                onChange={e => setForm(f => f && { ...f, code: e.target.value })} />
              <TextField id="ap-name" label="Nome *" maxLength={150} value={form.name} disabled={saving}
                onChange={e => setForm(f => f && { ...f, name: e.target.value })} />
              <Select id="ap-event" label="Evento *" value={form.event} disabled={saving}
                onChange={e => setForm(f => f && { ...f, event: e.target.value as ActivityPayoutEvent })}>
                {EVENTS.map(ev => <option key={ev} value={ev}>{ACTIVITY_EVENT_LABELS[ev]}</option>)}
              </Select>
              <Select id="ap-ben" label="Beneficiario *" value={form.beneficiary} disabled={saving}
                onChange={e => setForm(f => f && { ...f, beneficiary: e.target.value as ActivityPayoutBeneficiary })}>
                {BENEFICIARIES.map(b => <option key={b} value={b}>{ACTIVITY_BENEFICIARY_LABELS[b]}</option>)}
              </Select>
              <TextField id="ap-amount" label="Importo fisso (€) *" inputMode="decimal" placeholder="30,00" value={form.amount}
                className="md-span-full"
                disabled={saving} onChange={e => setForm(f => f && { ...f, amount: e.target.value })}
                supportingText="Esempi: €5 ad appuntamento, €20 a contratto generato, €30 a contratto attivato." />
            </FormSection>
            <FormSection title="Ambito">
              <Select id="ap-dept" label="Reparto" value={form.departmentId} disabled={saving}
                onChange={e => setForm(f => f && { ...f, departmentId: e.target.value })}>
                <option value="">Tutti</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </Select>
              <TextField id="ap-prod" label="Id prodotto" type="number" min={1} placeholder="Tutti" value={form.productId}
                disabled={saving} onChange={e => setForm(f => f && { ...f, productId: e.target.value })} />
              <TextField id="ap-prio" label="Priorità" type="number" min={0} max={1000} value={form.priority}
                className="md-span-full"
                disabled={saving} onChange={e => setForm(f => f && { ...f, priority: e.target.value })}
                supportingText="A parità di specificità vince la più alta. Prodotto e reparto indicati rendono la regola più specifica." />
            </FormSection>
            <FormSection title="Periodo">
              <TextField id="ap-from" label="Valida dal *" type="date" value={form.validFrom} disabled={saving}
                onChange={e => setForm(f => f && { ...f, validFrom: e.target.value })} />
              <TextField id="ap-to" label="Fino al (incluso)" type="date" value={form.validTo} disabled={saving}
                onChange={e => setForm(f => f && { ...f, validTo: e.target.value })} />
            </FormSection>
          </>
        )}
      </Dialog>
    </div>
  )
}

// ---- Esecuzione -----------------------------------------------------------------------------------

function RunTab() {
  const [from, setFrom] = useState(monthStart())
  const [to, setTo] = useState(isoDay(new Date()))
  const [result, setResult] = useState<ActivityPayoutRunResult | null>(null)
  const [previewKey, setPreviewKey] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const key = `${from}|${to}`
  const canExecute = result?.dryRun === true && previewKey === key && !busy

  async function run(dryRun: boolean) {
    setBusy(true)
    setError(null)
    try {
      const res = await activityPayoutsApi.run(from, to, dryRun)
      setResult(res)
      setPreviewKey(dryRun ? key : null)
    } catch (e) {
      setError(errorText(e, 'Esecuzione non riuscita'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="d-flex flex-column gap-3">
      <Card variant="outlined">
        <CardContent>
          <FormGrid columns={4} className="md-form-grid--end">
            <div>
              <TextField id="run-from" label="Dal" type="date" value={from} onChange={e => setFrom(e.target.value)} />
            </div>
            <div>
              <TextField id="run-to" label="Al (incluso)" type="date" value={to} onChange={e => setTo(e.target.value)} />
            </div>
            <div className="md-span-2 d-flex gap-2">
              <Button variant="outlined" disabled={busy || !from || !to} onClick={() => void run(true)}>
                Anteprima (nessuna scrittura)
              </Button>
              <Button variant="filled" disabled={!canExecute} onClick={() => void run(false)}>
                Esegui
              </Button>
            </div>
          </FormGrid>
          <p className="text-muted md-typescale-body-small mt-2 mb-0">
            Si può eseguire solo dopo l&apos;anteprima dello stesso periodo (massimo 366 giorni). Rieseguire è sicuro:
            il maturato già presente non si duplica. Le pratiche stornate dopo l&apos;evento passano a Stornato.
          </p>
        </CardContent>
      </Card>

      {error && <Callout tone="error">{error}</Callout>}

      {result && (
        <Card variant="outlined">
          <CardHeader
            title={`${result.dryRun ? 'Anteprima' : 'Esecuzione completata'} — ${result.from} → ${result.to}`}
            headingLevel={3}
          />
          <CardContent>
            <p className="mb-2">Regole attive: <strong>{result.activeRules}</strong></p>
            <ul className="mb-3">
              <li>Appuntamenti effettuati: {result.events.appuntamentoEffettuato}</li>
              <li>Contratti generati: {result.events.contrattoGenerato}</li>
              <li>Contratti attivati: {result.events.contrattoAttivato}</li>
            </ul>
            <StatGrid>
              <Stat label={result.dryRun ? 'Nuovo maturato' : 'Maturato creato'} value={String(result.newAccruals)} />
              <Stat label="Già presente" value={String(result.alreadyPresent)} />
              <Stat label="Passati a Stornato" value={String(result.stornoApplied)} />
              <Stat label="Scartati" value={String(result.discardedCount)} />
              <Stat label="Importo nuovo" value={money(result.newAmount)} />
            </StatGrid>
            {result.discardedCount > 0 && (
              <>
                <h4 className="md-typescale-title-small">Scarti</h4>
                <DataTable variant="plain">
                  <thead><tr><th>Evento</th><th>Fonte</th><th>Beneficiario</th><th>Regola</th><th>Motivo</th></tr></thead>
                  <tbody>
                    {result.discarded.map((d, i) => (
                      <tr key={`${d.sourceType}-${d.sourceId}-${d.ruleCode}-${i}`}>
                        <td>{ACTIVITY_EVENT_LABELS[d.event] ?? d.event}</td>
                        <td>{d.sourceType === 'Contract' ? 'Pratica' : 'Appuntamento'} #{d.sourceId}</td>
                        <td>{ACTIVITY_BENEFICIARY_LABELS[d.beneficiary] ?? d.beneficiary}</td>
                        <td>{d.ruleCode}</td>
                        <td>{d.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
                {result.discardedCount > result.discarded.length && (
                  <p className="text-muted md-typescale-body-small">Mostrati i primi {result.discarded.length} scarti.</p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return <StatCard label={label} value={value} />
}

// ---- Maturato -------------------------------------------------------------------------------------

function AccrualsTab({ admin, userLabel }: { admin: boolean; userLabel: (id: number) => string }) {
  const [from, setFrom] = useState(monthStart())
  const [to, setTo] = useState(isoDay(new Date()))
  const [beneficiary, setBeneficiary] = useState('')
  const [data, setData] = useState<ActivityPayoutList | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await activityPayoutsApi.list({
        from: from || undefined,
        to: to || undefined,
        beneficiaryId: admin && beneficiary ? Number(beneficiary) : undefined,
      }))
    } catch (e) {
      setError(errorText(e, 'Errore nel caricamento'))
    } finally {
      setLoading(false)
    }
  }, [from, to, beneficiary, admin])

  useEffect(() => {
    void load()
  }, [load])

  const sourceText = useMemo(
    () => (t: string, id: number) => `${t === 'Contract' ? 'Pratica' : 'Appuntamento'} #${id}`,
    [],
  )

  return (
    <div className="d-flex flex-column gap-3">
      <FilterBar>
        <TextField id="ac-from" label="Dal" type="date" value={from} onChange={e => setFrom(e.target.value)} />
        <TextField id="ac-to" label="Al" type="date" value={to} onChange={e => setTo(e.target.value)} />
        {admin && (
          <TextField id="ac-ben" label="Id beneficiario" type="number" min={1} placeholder="Tutti" value={beneficiary}
            onChange={e => setBeneficiary(e.target.value)} />
        )}
      </FilterBar>

      {error && <Callout tone="error">{error}</Callout>}

      {data?.totalMaturato !== undefined && (
        <StatGrid>
          <Stat label="Totale maturato" value={money(data.totalMaturato)} />
          <Stat label="Totale stornato" value={money(data.totalStornato)} />
        </StatGrid>
      )}

      {loading ? (
        <LoadingState />
      ) : (
        <DataTable>
          <thead>
            <tr>
              <th>Data</th><th>Beneficiario</th><th>Evento</th><th>Fonte</th><th>Regola</th><th className="md-num">Importo</th><th>Stato</th>
            </tr>
          </thead>
          <tbody>
            {(!data || data.items.length === 0) && <DataTableEmptyRow colSpan={7} icon="payments" title="Nessun maturato nel periodo." />}
            {data?.items.map(a => (
              <tr key={a.id}>
                <td>{a.competenceDate}</td>
                <td>{userLabel(a.beneficiaryUserId)}</td>
                <td>{ACTIVITY_EVENT_LABELS[a.event] ?? a.event}</td>
                <td>{sourceText(a.sourceType, a.sourceId)}</td>
                <td>{a.ruleCode} v{a.ruleVersion}</td>
                <td className="md-num">{money(a.amount)}</td>
                <td>
                  <StatusPill tone={STATUS_TONE[a.status] ?? 'neutral'}>{a.status}</StatusPill>
                  {a.stornoReason && <div className="md-typescale-body-small text-muted">{a.stornoReason}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
      {data?.truncated && <p className="text-muted md-typescale-body-small mt-2">Mostrate le prime {data.items.length} righe: restringi il periodo.</p>}
    </div>
  )
}
